/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {changeUserPassword} from '@ion/core/src/services/Connect';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseChangePasswordResult = UseDefaultQueryResult & {
  save: (id: number, data: object) => void;
};

const api = async({id, data}) => {
  try {
    const created = await changeUserPassword(id, data);
    return created;
  } catch (err) {
    throw new Error(`An ${err.status || ''} Error Occurred`);
  }
};

/**
 * Common hook for Changing user password.
 *
 * ```typescript
 *
 *    import useChangePassword from '@ion/query/src/hooks/';
 *
 *    const {save, isSuccess, isError} = useChangePassword(password);
 *
 * ```
 */
export const useChangePassword = (id: number): UseChangePasswordResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSettled : () => {
      if (id) {
        queryClient.invalidateQueries([QueryKey.USER, id]);
      }
    }
  });
  const save = useCallback((id: number, data: object) => {
    mutation.mutate({id, data});
  }, [mutation]);
  return createMutationResult(
    mutation, {save}
  ) as UseChangePasswordResult;
};

export default useChangePassword;
